import React, {
  FunctionComponent,
  useCallback,
  useState,
  ChangeEvent,
  useEffect,
} from 'react';
import { useForm, Controller } from 'react-hook-form';

import addressApi from 'scenes/common/api/address';
import { SubmitButton } from 'components';
import { FormGroup } from 'components/chroma';
import {
  AutocompleteAddress,
  CANNOT_FIND_ADDRESS,
} from 'components/chroma/extended';

import {
  AustralianState,
  PostalAddress,
  LocalityAddress,
  ApiData,
  Address,
} from 'scenes/common/types';
import {
  getApiData,
  isApiError,
  makeApiSuccess,
} from 'scenes/common/helpers/apiData';
import { InputQuestion } from 'scenes/common/components/Questions';
import {
  AddressNotFoundModal,
  ScreenReaderAddressInstruction,
} from 'scenes/common/components/Steps/Addresses/components';
import { AddressStepProps } from '../types';

const MANUAL_ADDRESS_SUBURB = 'manualAddressSuburb';
const MANUAL_ADDRESS_STREET = 'manualAddressStreet';

export const SUBURB_QUESTION = 'What is the postcode or suburb?';
export const SUBURB_ERROR_MESSAGE = 'Please select your postal address';
export const SUBURB_LABEL = 'Postcode or Suburb';
export const ADDRESS_QUESTION = 'What is the street address?';
export const ADDRESS_LABEL = 'Postal Address';

const ADDRESS_ERROR = 'address-error';
const MIN_LENGTH = 4;
const MAX_LENGTH = 100;
const VALID_PATTERN = /([A-Za-z0-9/\\,.-])/;

type AddressOptionType = {
  label: string;
  value: PostalAddress;
};

const IDLE = makeApiSuccess(undefined);

const displayLocality = (address: PostalAddress | undefined) =>
  address ? address.addressLine2 : '';

type ManualAddressValues = {
  selectedLocality: LocalityAddress;
  streetAddress: string;
};
const toPostalAddress = ({
  selectedLocality: locality,
  streetAddress: address,
}: ManualAddressValues): PostalAddress => ({
  address: `${address}, ${locality.addressLine2}`,
  addressLine1: address,
  addressLine2: locality.addressLine2,
  postcode: locality.postcode,
  state: locality.state,
  suburb: locality.suburb,
});

const toLocality = (locality: PostalAddress | undefined): LocalityAddress => ({
  address: locality?.address ?? '',
  addressLine1: '',
  addressLine2: locality?.addressLine2 ?? '',
  postcode: locality?.postcode ?? '',
  state: locality?.state ?? AustralianState.ACT,
  suburb: locality?.suburb ?? '',
});

const toAddress = (locality: PostalAddress | undefined): string =>
  locality?.addressLine1 ?? '';

const toAddressOptions = (
  addresses: ApiData<Address[] | undefined>
): AddressOptionType[] | undefined =>
  getApiData(addresses)?.map((locality: Address) => ({
    label: displayLocality(locality),
    value: locality,
  }));

const DifferentPostalAddressManualStep: FunctionComponent<AddressStepProps<
  PostalAddress
>> = ({ defaultStateValue, onNext, onClickCallUs, onApiError }) => {
  const {
    formState: hooksForm,
    setError,
    getValues,
    clearErrors,
    handleSubmit,
    errors,
    control,
  } = useForm({
    mode: 'onChange',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [localityList, setLocalityList] = useState<
    ApiData<Address[] | undefined>
  >(IDLE);
  const [showModal, setShowModal] = useState(false);
  const [selectedLocality, setSelectedLocality] = useState<
    LocalityAddress | undefined
  >(toLocality(defaultStateValue));
  const [streetAddress, setStreetAddress] = useState(
    toAddress(defaultStateValue)
  );

  const onSubmit = useCallback(() => {
    onNext({
      postalAddress: toPostalAddress({
        selectedLocality: selectedLocality as LocalityAddress,
        streetAddress,
      }),
    });
  }, [onNext, selectedLocality, streetAddress]);

  const setAddressError = useCallback(
    (message: string = ''): void => {
      setError(ADDRESS_ERROR, {
        type: 'manual',
        message,
      });
    },
    [setError]
  );

  const handleSuburbChange = useCallback(
    async (value: string, onChange: Function): Promise<void> => {
      onChange(value);
      if (value?.length >= MIN_LENGTH) {
        setIsLoading(true);
      }
    },
    []
  );

  const handleSuburbClear = useCallback(
    (onChange: Function): void => {
      onChange('');
      clearErrors();
      setLocalityList(IDLE);
    },
    [clearErrors]
  );

  const handleSuburbSelect = useCallback(
    (addressOption: AddressOptionType, onChange: Function): void => {
      if (addressOption.label === CANNOT_FIND_ADDRESS) {
        setShowModal(true);
      } else {
        onChange(addressOption.label);
        clearErrors();
        setLocalityList(IDLE);
        setSelectedLocality(addressOption.value);
      }
    },
    [clearErrors]
  );

  const handleBlur = useCallback(
    (value: string): void => {
      const errorMessage =
        value?.length >= MIN_LENGTH ? SUBURB_ERROR_MESSAGE : '';
      setAddressError(errorMessage);
      setLocalityList(IDLE);
    },
    [setAddressError]
  );

  const handleAddressChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function): void => {
      const { value } = event.target;

      if (!value || value.match(VALID_PATTERN)) {
        setStreetAddress(value);
        onChange(value);
      }
    },
    []
  );

  useEffect(() => {
    let componentMounted = true;

    if (isLoading) {
      setAddressError();
      setSelectedLocality(undefined);
      addressApi()
        .getLocalities(getValues(MANUAL_ADDRESS_SUBURB))
        .then((localities) => {
          if (isApiError(localities)) {
            onApiError(`Address api: ${localities.errorReason?.message || ''}`);
          }
          if (componentMounted) {
            setLocalityList(
              isApiError(localities) ? makeApiSuccess([]) : localities
            );
            setIsLoading(false);
          }
        });
    }

    return () => {
      componentMounted = false;
    };
  }, [getValues, isLoading, onApiError, setAddressError]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ScreenReaderAddressInstruction items={getApiData(localityList)} />
      <Controller
        control={control}
        name={MANUAL_ADDRESS_SUBURB}
        defaultValue={selectedLocality?.addressLine2 ?? ''}
        rules={{ required: true, minLength: MIN_LENGTH }}
        render={({ onChange, value }) => (
          <FormGroup
            id={MANUAL_ADDRESS_SUBURB}
            label={SUBURB_QUESTION}
            error={errors[ADDRESS_ERROR]?.message || ''}
          >
            <AutocompleteAddress
              id={MANUAL_ADDRESS_SUBURB}
              name={MANUAL_ADDRESS_SUBURB}
              placeholder="e.g. 3000"
              ariaLabel={SUBURB_LABEL}
              value={value}
              values={toAddressOptions(localityList)}
              valid={!errors[ADDRESS_ERROR] && Boolean(value)}
              error={Boolean(errors[ADDRESS_ERROR]) && Boolean(value)}
              loading={isLoading}
              autoComplete="on"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleSuburbChange(event.target.value, onChange)
              }
              onClear={() => handleSuburbClear(onChange)}
              onSelect={(_: Event, listItem: AddressOptionType) =>
                handleSuburbSelect(listItem, onChange)
              }
              onBlur={() => handleBlur(value)}
            />
          </FormGroup>
        )}
      />

      <InputQuestion
        stateKey={MANUAL_ADDRESS_STREET}
        errors={errors}
        control={control}
        question={ADDRESS_QUESTION}
        ariaLabel={ADDRESS_LABEL}
        defaultValue={streetAddress ?? ''}
        placeholder="e.g. Unit 8 12 Sample Street"
        changeHandler={handleAddressChange}
        validationRules={{
          required: true,
          maxLength: MAX_LENGTH,
          pattern: VALID_PATTERN,
        }}
        ariaRequired
      />

      <SubmitButton enabled={hooksForm.isValid}>Next</SubmitButton>
      <AddressNotFoundModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onClickCallUs={onClickCallUs}
      />
    </form>
  );
};

export default DifferentPostalAddressManualStep;
